import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from './swal';
import RoadiesShell from './RoadiesShell';
import { clearGameTimer, useIsMobile } from './hooks';

// index.php: the landing page is the intro video with a green "BEGIN PLAY" button; the end of the
// clip (or the button) goes to stage 1. The two clips are the ones the PHP page switched between.
// They are served from S3 under the same names (<REACT_APP_S3_PATH>video-new.mp4 /
// video-new-mobile.mp4); the copies bundled in /roadies/media are the fallback if S3 can't be reached.
const S3_BASE = process.env.REACT_APP_S3_PATH ? process.env.REACT_APP_S3_PATH.replace(/\/?$/, '/') : '';
const clip = (name) => ({ remote: S3_BASE ? `${S3_BASE}${name}` : '', local: `/roadies/media/${name}` });
const DESK_VIDEO = clip('video-new.mp4');
const MOB_VIDEO = clip('video-new-mobile.mp4');

// The animated three-chevron arrow from index.php's .btn-content.
const Arrow = () => (
  <span className="rd-begin-arrow">
    <svg width="66px" height="43px" viewBox="0 0 66 43" xmlns="http://www.w3.org/2000/svg">
      <g id="arrow" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <path id="rd-arrow-1" d="M40.1543933,3.89485454 L43.9763149,0.139296592 C44.1708311,-0.0518420739 44.4826329,-0.0518571125 44.6771675,0.139262789 L65.6916134,20.7848311 C66.0855801,21.1718824 66.0911863,21.8050225 65.704135,22.1989893 C65.7000188,22.2031791 65.6958657,22.2073326 65.6916762,22.2114492 L44.677098,42.8607841 C44.4825957,43.0519059 44.1708242,43.0519358 43.9762853,42.8608513 L40.1545186,39.1069479 C39.9575152,38.9134427 39.9546793,38.5968729 40.1481845,38.3998695 C40.1502893,38.3977268 40.1524132,38.395603 40.1545562,38.3934985 L56.9937789,21.8567812 C57.1908028,21.6632968 57.193672,21.3467273 57.0001876,21.1497035 C56.9980647,21.1475418 56.9959223,21.1453995 56.9937605,21.1432767 L40.1545208,4.60825197 C39.9574869,4.41477773 39.9546013,4.09820839 40.1480756,3.90117456 C40.1501626,3.89904911 40.1522686,3.89694235 40.1543933,3.89485454 Z" fill="#FFFFFF" />
        <path id="rd-arrow-2" d="M20.1543933,3.89485454 L23.9763149,0.139296592 C24.1708311,-0.0518420739 24.4826329,-0.0518571125 24.6771675,0.139262789 L45.6916134,20.7848311 C46.0855801,21.1718824 46.0911863,21.8050225 45.704135,22.1989893 C45.7000188,22.2031791 45.6958657,22.2073326 45.6916762,22.2114492 L24.677098,42.8607841 C24.4825957,43.0519059 24.1708242,43.0519358 23.9762853,42.8608513 L20.1545186,39.1069479 C19.9575152,38.9134427 19.9546793,38.5968729 20.1481845,38.3998695 C20.1502893,38.3977268 20.1524132,38.395603 20.1545562,38.3934985 L36.9937789,21.8567812 C37.1908028,21.6632968 37.193672,21.3467273 37.0001876,21.1497035 C36.9980647,21.1475418 36.9959223,21.1453995 36.9937605,21.1432767 L20.1545208,4.60825197 C19.9574869,4.41477773 19.9546013,4.09820839 20.1480756,3.90117456 C20.1501626,3.89904911 20.1522686,3.89694235 20.1543933,3.89485454 Z" fill="#FFFFFF" />
        <path id="rd-arrow-3" d="M0.154393339,3.89485454 L3.97631488,0.139296592 C4.17083111,-0.0518420739 4.48263286,-0.0518571125 4.67716753,0.139262789 L25.6916134,20.7848311 C26.0855801,21.1718824 26.0911863,21.8050225 25.704135,22.1989893 C25.7000188,22.2031791 25.6958657,22.2073326 25.6916762,22.2114492 L4.67709797,42.8607841 C4.48259567,43.0519059 4.17082418,43.0519358 3.97628526,42.8608513 L0.154518591,39.1069479 C-0.0424848215,38.9134427 -0.0453206733,38.5968729 0.148184538,38.3998695 C0.150289256,38.3977268 0.152413239,38.395603 0.154556228,38.3934985 L16.9937789,21.8567812 C17.1908028,21.6632968 17.193672,21.3467273 17.0001876,21.1497035 C16.9980647,21.1475418 16.9959223,21.1453995 16.9937605,21.1432767 L0.15452076,4.60825197 C-0.0425130651,4.41477773 -0.0453986756,4.09820839 0.148075568,3.90117456 C0.150162624,3.89904911 0.152268631,3.89694235 0.154393339,3.89485454 Z" fill="#FFFFFF" />
      </g>
    </svg>
  </span>
);

export default function RoadiesIndex() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const mobile = useIsMobile();
  const { session, game } = useSelector((s) => s.roadies);
  const videoRef = useRef(null);
  const [clipSrc] = useState(mobile ? MOB_VIDEO : DESK_VIDEO);
  const [src, setSrc] = useState(clipSrc.remote || clipSrc.local);
  // Always start playing the moment the page opens. Browsers only guarantee autoplay for a muted clip, so the
  // video starts muted and is un-muted straight away; if the browser refuses sound (no click on the site
  // yet) it keeps playing muted and the sound comes on at the first tap / key press.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const events = ['pointerdown', 'touchstart', 'keydown'];
    const unmute = () => {
      v.muted = false;
      v.volume = 1;
      if (v.paused) v.play().catch(() => {});
      events.forEach((e) => window.removeEventListener(e, unmute));
    };
    const tryUnmute = () => {
      v.muted = false;
      v.volume = 1;
      // a browser that refuses sound pauses the clip: put it back on muted and wait for a gesture
      setTimeout(() => {
        if (v.paused && !v.ended) {
          v.muted = true;
          v.play().catch(() => {});
          events.forEach((e) => window.addEventListener(e, unmute));
        }
      }, 250);
    };
    const start = () => {
      v.muted = true;
      v.play().then(tryUnmute).catch(() => events.forEach((e) => window.addEventListener(e, unmute)));
    };
    if (v.readyState >= 2) start();
    else v.addEventListener('canplay', start, { once: true });
    return () => {
      v.removeEventListener('canplay', start);
      events.forEach((e) => window.removeEventListener(e, unmute));
    };
  }, [src]);

  const begin = () => navigate({ pathname: '/stage1', search });

  // index.php deleted the "gettime" cookie: the clock restarts from the saved time.
  useEffect(() => {
    if (session) clearGameTimer(session);
  }, [session]);

  // Players who already finished all stages are turned away (index.php's s8 check).
  useEffect(() => {
    if (game?.gameover) {
      Swal.fire({ text: 'You have played this game already', icon: 'error', allowOutsideClick: false }).then(() => {
        const base = process.env.REACT_APP_BASE_URL;
        window.location.href = session?.gameId ? `${base}/game-detail/${session.gameId}` : session?.backButtonRedirect || base;
      });
    }
  }, [game, session]);

  return (
    <RoadiesShell bgColor="#000" className="rd-index">
      <div className="rd-index-inner">
        <video
          ref={videoRef}
          className="rd-intro-video"
          src={src}
          autoPlay
          muted
          playsInline
          preload="auto"
          controls={false}
          onError={() => src !== clipSrc.local && setSrc(clipSrc.local)}
          onEnded={begin}
        />
        {session && (
          <div className="rd-begin-holder">
            <button type="button" className="rd-begin" onClick={begin}>
              <span className="rd-begin-text">BEGIN PLAY</span>
              <Arrow />
            </button>
          </div>
        )}
      </div>
    </RoadiesShell>
  );
}
